import { useMemo } from 'react'
import EChartCustomized, {
    SCHEMES,
    TOOLTIP_OPTIONS,
} from '@/components/EchartCustomized'

interface DowntimeBreakdownChartProps {
    providerDowntime: number
    thirdPartyDowntime: number
    scheduledDowntime: number
    customerDowntime: number
}

// Format minutes to readable string
const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

/**
 * Pie chart showing downtime breakdown by cause
 */
const DowntimeBreakdownChart: React.FC<DowntimeBreakdownChartProps> = ({
    providerDowntime,
    thirdPartyDowntime,
    scheduledDowntime,
    customerDowntime,
}) => {
    const total =
        providerDowntime +
        thirdPartyDowntime +
        scheduledDowntime +
        customerDowntime

    const options = useMemo(() => {
        if (total === 0) return null

        return {
            tooltip: {
                trigger: 'item',
                ...TOOLTIP_OPTIONS,
                formatter: (params: any) => {
                    return `<b>${params.name}</b><br/>
                        Thời gian: ${formatMinutes(params.value)}<br/>
                        Tỷ lệ: ${params.percent}%`
                },
            },
            legend: {
                orient: 'horizontal',
                bottom: 0,
                left: 'center',
                textStyle: { fontSize: 10 },
                itemWidth: 12,
                itemHeight: 12,
            },
            series: [
                {
                    name: 'Downtime Breakdown',
                    type: 'pie',
                    radius: ['0%', '65%'],
                    center: ['50%', '42%'],
                    roseType: 'radius',
                    itemStyle: {
                        borderRadius: 4,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        formatter: (params: any) => formatMinutes(params.value),
                        fontSize: 10,
                    },
                    labelLine: {
                        length: 8,
                        length2: 6,
                    },
                    data: [
                        {
                            value: providerDowntime,
                            name: 'Provider',
                            itemStyle: { color: SCHEMES.error[0] },
                        },
                        {
                            value: thirdPartyDowntime,
                            name: 'Third-party',
                            itemStyle: { color: SCHEMES.warning[0] },
                        },
                        {
                            value: scheduledDowntime,
                            name: 'Bảo trì',
                            itemStyle: { color: SCHEMES.info[0] },
                        },
                        {
                            value: customerDowntime,
                            name: 'Khách hàng',
                            itemStyle: { color: SCHEMES.brand[5] },
                        },
                    ].filter((item) => item.value > 0),
                },
            ],
        }
    }, [
        providerDowntime,
        thirdPartyDowntime,
        scheduledDowntime,
        customerDowntime,
        total,
    ])

    if (!options) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Không có downtime
            </div>
        )
    }

    return (
        <div style={{ height: '220px' }}>
            {/* @ts-ignore */}
            <EChartCustomized options={options} />
        </div>
    )
}

export default DowntimeBreakdownChart
